import CardCommon from '@op/components/CardCommon';
import {
  barIcon,
  commonBarHIcon,
  lineIcon,
  pieIcon,
  tableIcon,
} from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Segment } from 'tea-component';
import { useVideoTagInsight } from '../../hooks';
import { DBO_V } from '@op/utils/format';
import TableCommonHook from '@op/components/TableCommonHook';
import { formatFilterDate } from '@op/utils/time';
import './index.scss';
import { dealWithOthersName } from '@op/utils/languageValue';
import { METRIC_OPTIONS, TagOption } from '../TagAnalysisCard';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import classnames from 'classnames';
import PieChart from './PieChart';
import CommonBarChart from './CommonBarChart';
import BarLineChart from './BarLineChart';
import DetailWrapper from '@op/pages/Detail/component/DetailWrapper';
import { ClassSpecial } from '@op/constants/common';
import { EIGHTBALL_EDITION_ID } from '../utils';
import { defaultNavigator } from '../../pages/VideoTagInsight';

// Constants
const DEFAULT_TOP_N = 20;
const OVERVIEW_TITLE = 'Overview';

interface FilterParams {
  video_tags?: Array<{
    t1: string;
    t2: string[];
  }>;
  video_tags_tier: string;
  video_tags_top_n: number;
  metric: string;
  group_bys: string[];
  [key: string]: any;
}

interface ITagAnalysisChart {
  filter: any;
  title: string;
  height?: number;
  defaultTier?: string;
  attributeBy: string;
  metric: string;
  tagOptions?: TagOption[];
  titleCn?: string;
  isSpecialEdition?: boolean;
  isDetail?: boolean;
  updateDetailFilter?: (filter: any) => void;
  detailPage?: boolean;
  defaultChartType?: any;
  defaultSelected?: any;
}

export default function TagAnalysisChart(props: ITagAnalysisChart) {
  const {
    title,
    defaultTier = 't2',
    height = 350,
    filter,
    attributeBy,
    metric,
    tagOptions,
    titleCn,
    isSpecialEdition = false,
    isDetail = false,
    updateDetailFilter,
    detailPage = false,
    defaultChartType,
    defaultSelected
  } = props;

  const eChartsRef = useRef<any>(null);

  const getDefaultChartType = useCallback(() => {
    switch (attributeBy) {
      case 'total':
        return isSpecialEdition ? 'common-bar' : 'pie';
      case 'time':
        return 'line';
      case 'language':
        return 'common-bar';
      case 'platform':
        return 'bar';
      default:
        return 'bar';
    }
  }, [attributeBy, isSpecialEdition]);

  const [chartType, setChartType] = useState(defaultChartType || getDefaultChartType());
  const [videoTagsTier, setVideoTagsTier] = useState(defaultTier);
  const { formatMessage, isEn } = useLanguage();
  const { download, downloading } = useDownloadParams();

  const filterParams = useMemo(() => {
    if (!filter) {
      return null;
    }
    const groupBy = attributeBy === 'total' ? [] : [attributeBy];
    const newFilter: FilterParams = {
      ...filter,
      video_tags_tier: videoTagsTier,
      video_tags_top_n: DEFAULT_TOP_N,
      metric,
      group_bys: groupBy,
    };

    if (title !== OVERVIEW_TITLE) {
      let videoTags = filter?.video_tags?.filter(
        (val: any) => val.t1 === title,
      );
      if (!videoTags?.length && tagOptions?.length) {
        videoTags = tagOptions
          .filter((val) => val.label_en === title)
          .map((val) => ({
            t1: val.label_en,
            t2: [],
          }));
      }

      return {
        ...newFilter,
        video_tags: videoTags || [],
      };
    }
    return newFilter;
  }, [filter, attributeBy, metric, videoTagsTier, title, tagOptions]);

  // Reset chart type when filter changes
  useEffect(() => {
    setChartType(getDefaultChartType());
  }, [getDefaultChartType]);

  const xAxisKey = useMemo(
    () => (attributeBy === 'total' ? 'time' : attributeBy),
    [attributeBy],
  );

  const { data, loading } = useVideoTagInsight({
    filter: filterParams,
    extraData: { xAxisKey },
  });

  const operation = useMemo(() => {
    switch (attributeBy) {
      case 'total':
        return [commonBarHIcon, pieIcon, tableIcon];
      case 'platform':
        return [barIcon, tableIcon];
      case 'language':
        return [commonBarHIcon, tableIcon];
      default:
        return [lineIcon, barIcon, tableIcon];
    }
  }, [attributeBy]);

  const tagList = useMemo(() => data?.list || [], [data?.list]);

  const translateTags = useCallback(
    (key: string) => {
      const tag = tagList.find((val) => val.label_en === key);
      if (tag) {
        return isEn ? tag.label_en : tag.label_zh;
      }
      return formatMessage(key);
    },
    [isEn, tagList, formatMessage],
  );

  const buildVideoTags = useCallback(
    (tagName: string): Array<{ t1: string; t2: string[] }> => {
      if (title === OVERVIEW_TITLE) {
        if (videoTagsTier === 't1') {
          return [{ t1: tagName, t2: [] }];
        }
        const parentTag = tagList.find((val) =>
          val.children?.some((child: any) => child.label_en === tagName),
        );
        if (parentTag) {
          return [{
            t1: parentTag.label_en, t2: [tagName],
            /// add new key, keep the label_en , label_zh here? 
          }];
        }
        return [];
      }
      return [{ t1: title, t2: [tagName] }];
    },
    [title, videoTagsTier, tagList],
  );

  const formatParamsKey = useCallback(
    (value: string, key: string) => {
      if (key === 'video_tags') {
        const videoTags = buildVideoTags(value);
        return { video_tags: videoTags };
      }
      return value;
    },
    [buildVideoTags],
  );

  const columns = useMemo(() => {
    const columnConfig = {
      language: {
        key: 'language',
        header: formatMessage({ id: 'Language' }),
        render: (cvm: any) => dealWithOthersName(cvm.language, 'language'),
        width: 180,
      },
      platform: {
        key: 'platform',
        header: formatMessage({ id: 'Platform' }),
        render: (cvm: any) => dealWithOthersName(cvm.platform, 'channel'),
        width: 150,
      },
      default: {
        key: 'time',
        header: formatMessage({ id: 'Date' }),
        render: (cvm: any) => formatFilterDate(cvm.time, filter),
        width: 150,
      },
    };

    const dynamicColumn = {
      ...(columnConfig[attributeBy as keyof typeof columnConfig] ||
        columnConfig.default),
      align: 'left' as const,
    };

    const getTierText = () => {
      const id = videoTagsTier === 't1' ? 'Tier 1 Tags' : 'Tier 2 Tags';
      return formatMessage({ id });
    };

    if (videoTagsTier === 't1' && attributeBy !== 'total') {
      const tagsColumns =
        data?.legendData?.map((val) => ({
          key: val,
          header: translateTags(val),
          render: (cvm: any) => DBO_V.reviews(cvm?.[val]) || '-',
        })) || [];

      return [dynamicColumn, ...tagsColumns];
    }

    const valueText =
      METRIC_OPTIONS.find((option) => option.value === metric)?.text || 'Value';

    const fixedColumns = [
      {
        key: 'label',
        header: getTierText(),
        align: 'left' as const,
        render: (cvm: any) => (
          <div className="dbo-label-tier-text-box">
            {translateTags(cvm.label_en)}
          </div>
        ),
      },
      {
        key: 'value',
        header: translateTags(valueText),
        align: 'right' as const,
        render: (cvm: any) => DBO_V.reviews(cvm.value),
        width: 150,
      },
    ];

    // When attributeBy is 'total', don't show dynamic column and use pieData for table
    if (attributeBy === 'total') {
      return fixedColumns;
    }

    return [dynamicColumn, ...fixedColumns];
  }, [
    attributeBy,
    filter,
    formatMessage,
    translateTags,
    metric,
    videoTagsTier,
    data,
  ]);

  const tableRecords = useMemo(() => {
    if (videoTagsTier === 't1' && attributeBy !== 'total') {
      return data?.overviewTableData || [];
    }
    return attributeBy === 'total' ? data?.pieData : data?.tableData || [];
  }, [videoTagsTier, attributeBy, data]);

  const metricOptions = useMemo(() => METRIC_OPTIONS, []);

  const renderChart = useCallback((eventsDict?: any, onChartReady?: (chart: any) => void) => {
    const extraProps = { eventsDict, onChartReady, detailPage };

    if (chartType === 'table') {
      return (
        <TableCommonHook
          columns={columns}
          records={tableRecords}
          total={tableRecords?.length || 0}
          maxHeight={height - 50}
          hasPageable={false}
          minWidth={'auto'}
          className={title !== defaultNavigator ? ClassSpecial.TABLE_HIDE_CHECKBOX : ''}
          {...(eventsDict || {})}
        />
      );
    }

    if (chartType === 'pie') {
      return (
        <PieChart
          data={data}
          filter={filter}
          height={height}
          metric={metric}
          translateTags={translateTags}
          formatMessage={formatMessage}
          eChartsRef={eChartsRef}
          extraProps={extraProps}
          defaultSelected={defaultSelected}
        />
      );
    }

    if (chartType === 'common-bar') {
      return (
        <CommonBarChart
          data={data}
          filter={filter}
          height={height}
          xAxisKey={xAxisKey}
          metric={metric}
          translateTags={translateTags}
          formatMessage={formatMessage}
          metricOptions={metricOptions}
          attributeBy={attributeBy}
          extraProps={extraProps}
          defaultSelected={defaultSelected}
        />
      );
    }

    // bar, line, stack_line
    return (
      <BarLineChart
        data={data}
        filter={filter}
        height={height}
        chartType={chartType as 'bar' | 'line' | 'stack_line'}
        xAxisKey={xAxisKey}
        translateTags={translateTags}
        formatMessage={formatMessage}
        eChartsRef={eChartsRef}
        metric={metric}
        extraProps={extraProps}
        defaultSelected={defaultSelected}
      />
    );
  }, [chartType, columns]);

  const getTableParamsKey = (attributeBy: string) => {
    switch (attributeBy) {
      case 'total':
        return {
          video_tags: 'label_en'
        };
      case 'platform':
        return {
          video_tags: 'label_en',
          channel: 'platform'
        };
      case 'language':
        return {
          video_tags: 'label_en',
          language: 'language',
        };
      default:
        return {
          video_tags: 'label_en',
          start_time: 'time',
          end_time: 'time',
        };
    }
  };

  return (
    <CardCommon
      operations={operation}
      operationInfo={[chartType, setChartType]}
      loading={loading}
      loadingHeight={height}
      title={
        <div className={classnames('dbo-tag-analysis-chart-title-wrapper')}>
          <span className={classnames('analysis-chart-title')}>
            {isEn ? title : titleCn ?? formatMessage(title)}
          </span>
          {title === OVERVIEW_TITLE && (
            <Segment
              value={videoTagsTier}
              onChange={(value: 't1' | 't2') => {
                setVideoTagsTier(value);
                buriedBtnPoint('TAG_TIER_SELECT', { tier: value }, value);
              }}
              options={[
                {
                  text: formatMessage({ id: 'Tier 1 Tags' }),
                  value: 't1',
                },
                {
                  text: formatMessage({ id: 'Tier 2 Tags' }),
                  value: 't2',
                },
              ]}
              className="analysis-chart-segment"
            />
          )}
        </div>
      }
      className={classnames('dbo-tag-analysis-chart')}
      contentCard={!isDetail}
      hasDownload
      downloadLoading={downloading}
      handleDownloadOnClick={() => {
        download({
          key: 'TagAnalysisDownload',
          fileName: `Tag Analysis (${title})`,
          filter,
          auxiliaryParams: {
            title,
            metric,
            attributeBy,
            videoTagsTier,
            tagsList: data?.legendData,
          },
          data: (tableRecords || []).map((val) => ({
            ...val,
            title,
          })),
        });
      }}
      logBtnId="TAG_ANALYSIS_CHART"
      extraInfo={{ metric, title }}
      empty={data.isEmpty === true}
    >
      <DetailWrapper
        eventProps={{
          key: 'VideoTagAnalysis',
          defaultFilter: {
            ...(filterParams || {}),
            ...(isSpecialEdition ? { show_only_game_related_feeds: true } : {}),
          },
          defaultProps: { ...props, tagList, titleCn, defaultChartType: chartType, },
          paramsKey: {
            pie: { video_tags: 'name' },
            'common-bar': attributeBy === 'total' ? { video_tags: 'name' } : {
              video_tags: 'seriesName',
              language: 'name'
            },
            bar: attributeBy === 'date' ? {
              video_tags: 'seriesName',
              start_time: 'name',
              end_time: 'name',
            } : {
              video_tags: 'seriesName',
              channel: 'name'
            },
            line: {
              video_tags: 'seriesName',
              start_time: 'name',
              end_time: 'name',
            },
            table: getTableParamsKey(attributeBy),
          },
          chartType: chartType as any,
        }}
        detailPage={detailPage}
        isDetail={isDetail}
        updateDetailFilter={updateDetailFilter}
        formatParamsKey={formatParamsKey}
      >
        {({ eventsDict, onChartReady }) => renderChart(eventsDict, onChartReady)}
      </DetailWrapper>
    </CardCommon>
  );
}
